import { get } from 'lodash-es';
import { JOB_REPEAT_TYPE } from '~/app_constants';
import { formatInternalDate } from '~/utils/time_utils';

// We only need those as at maximum each month will have
// at most 4 weeks (and some of them a few extra days).
const ORDINALITIES = ['st', 'nd', 'rd', 'th'];

/**
 * Helper to get the recurring schedule label/tooltip in an
 * easy and human readable form.
 * It delegates to the "legacy code" version until we have
 * the schedules page converted to Vue.
 *
 * i.e:
 *   - "Every week on Monday, Wednesday"
 *   - "Every 3 weeks on Monday, Wednesday"
 *   - "Every month on the last Monday"
 *   - "Every month on the 1st Monday"
 *   - "Every month on the 12th"
 *   - "Every year on March 31st"
 *
 * @param {Object} schedule The `job_schedule` to derive the label/tooltip from
 * @returns String
 */
export function humanizeRecurringSchedule(schedule) {
  return window.RecurringScheduleUtils.humanizeRecurringSchedule(schedule);
}

/**
 * Helper function that given a `date` returns the the ordinality
 * nth number for that particular weekday within the date's month.
 *
 * i.e:
 *  - Given "2020-02-29" (leap year) it will return "last Saturday"
 *  - Given "2021-03-15" it will return [1, 2] & "3rd Monday"
 *  - Given "2021-03-22" it will return [1, 3] & "4th Monday"
 *  - Given "2021-03-29" it will return [1, -1] & "last Monday"
 *
 * @param {String} inputDate Date in the format of `YYYY-MM-DD`
 * @returns String
 */
export function getMonthlyNthDayForDate(inputDate) {
  const date = moment(inputDate);
  const start = date.clone().startOf('month').startOf('day');
  const end = date.clone().endOf('month').endOf('day');
  const daysDiff = end.diff(start, 'days');
  const dateMonth = start.clone();
  const day = date.day();
  const matches = [];
  let dateCounter = 0;
  let dayIndex = 0;

  for (let i = 0; i <= daysDiff; i++) {
    // If it's the same day, keep it
    if (dateMonth.day() === day) {
      const dateStr = formatInternalDate(moment(dateMonth));

      if (dateStr === inputDate) {
        dayIndex = dateCounter;
      }

      matches.push(dateStr);
      dateCounter++;
    }

    dateMonth.add(1, 'day');
  }

  const weekday = date.format('dddd');

  // If it's the last "Monday" of the month
  // use the 'last' format instead.
  if (dayIndex === matches.length - 1) {
    const data = [day, -1];

    return { data, text: `last ${weekday}` };
  }

  // Otherwise, use the ordinality based on the
  // day index that we got before.
  const ordinality = ORDINALITIES[dayIndex];
  const data = [day, dayIndex];

  return { data, text: `${dayIndex + 1}${ordinality} ${weekday}` };
}

export function repeatTypeIsRecurrent(repeatType) {
  return repeatType !== JOB_REPEAT_TYPE.Once;
}

export function jobIsRecurrent(job) {
  const repeatType = get(job, 'schedule.repeat_type', JOB_REPEAT_TYPE.Once);

  return repeatTypeIsRecurrent(repeatType);
}

/**
 * Helper function to create and empty `schedule`.
 *
 * @returns {Schedule} A default empty schedule
 */
export function defaultSchedule() {
  return {
    interval: 1,
    custom: false,
    weekdays: null,
    monthday: null,
    monthweekday: null,
    monthweekdaynum: null,
    repeat_type: JOB_REPEAT_TYPE.Once,
  };
}
